import React, { useState } from "react";
import {
  Box,
  Typography,
  Tab,
  Tabs,
  Paper,
  Button,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  alpha,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Business as BusinessIcon,
  Handshake as HandshakeIcon,
  ReceiptLong as ReceiptLongIcon,
  ListAlt as ListAltIcon,
  RequestQuote as RequestQuoteIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import FunderDialog from "../../components/FunderManagement/Dialog/FunderDialog";
import { deleteFunder, getFunders } from "../../api/services/Billing/funder";
import FunderRateCards from "../../components/FunderManagement/FunderRateCards";
import { QK } from "../../base/qk";
import ConfirmModal from "../../components/ConfirmModal";
import DataTable from "../../components/Table/DataTable";

const FunderManagement = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [activeTab, setActiveTab] = useState(0);
  const [councilSubTab, setCouncilSubTab] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedFunder, setSelectedFunder] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const category = activeTab === 0 ? "COUNCIL" : "BROKERAGE";

  const { data: funders = [], isLoading } = useQuery(
    [QK.funders, category],
    () => getFunders({ category }),
    { keepPreviousData: true }
  );

  const deleteMutation = useMutation(deleteFunder, {
    onSuccess: () => {
      queryClient.invalidateQueries(QK.funders);
      enqueueSnackbar("Funder deleted successfully", { variant: "success" });
      setConfirmOpen(false);
      setDeletingId(null);
    },
    onError: () => {
      enqueueSnackbar("Failed to delete funder", { variant: "error" });
      setConfirmOpen(false);
    },
  });

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setCouncilSubTab(0);
  };

  const handleSubTabChange = (event, newValue) => {
    setCouncilSubTab(newValue);
  };

  const handleAddClick = () => {
    setSelectedFunder(null);
    setIsDialogOpen(true);
  };

  const handleEditClick = (id) => {
    const funder = funders.find((f) => f.id === id);
    setSelectedFunder(funder);
    setIsDialogOpen(true);
  };

  const handleDeleteClick = (id) => {
    setDeletingId(id);
    setConfirmOpen(true);
  };

  const handleViewRates = () => {
    setActiveTab(0);
    setCouncilSubTab(1);
  };

  const handleConfirmDelete = () => {
    if (deletingId) deleteMutation.mutate(deletingId);
  };

  const headers = [
    { key: "name", label: activeTab === 0 ? "Council Authority" : "Agency Name" },
    { key: "billing_model", label: "Billing Model" },
    { key: "billing_cycle", label: "Claim Frequency" },
    { key: "payment_terms", label: "Payment Terms" },
    { key: "claim_format", label: "Integration" },
  ];

  const renderCell = (item, header) => {
    switch (header.key) {
      case "name":
        return (
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#1A1A2E" }}
            >
              {item.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                bgcolor: activeTab === 0 ? alpha("#2469F0", 0.08) : alpha("#F59E0B", 0.08),
                color: activeTab === 0 ? "#2469F0" : "#F59E0B",
                px: 1,
                borderRadius: 1,
                fontSize: "0.65rem",
                fontWeight: 600,
                mt: 0.5,
                display: "inline-block",
              }}
            >
              ID: {item.vendor_code || item.id}
            </Typography>
          </Box>
        );

      case "billing_model":
        return (
          <Typography sx={{ fontSize: "0.78rem", color: "#475467" }}>
            {activeTab === 0
              ? "Framework Rate"
              : `Negotiated (-${item.commission_rate}%)`}
          </Typography>
        );

      case "billing_cycle":
        return (
          <Typography sx={{ fontSize: "0.78rem", color: "#475467" }}>
            {item.billing_cycle}
          </Typography>
        );

      case "payment_terms":
        return (
          <Typography sx={{ fontSize: "0.78rem", color: "#475467" }}>
            Net {item.payment_terms} Days
          </Typography>
        );

      case "claim_format":
        return (
          <Chip
            label={item.claim_format}
            size="small"
            variant="outlined"
            sx={{
              fontWeight: 600,
              fontSize: "0.7rem",
              height: 22,
              color: activeTab === 0 ? "#2469F0" : "#F59E0B",
              borderColor:
                activeTab === 0 ? alpha("#2469F0", 0.4) : alpha("#F59E0B", 0.4),
            }}
          />
        );

      default:
        return item[header.key] || "-";
    }
  };

  const customActions = (item) => (
    <Stack direction="row" spacing={0.25} justifyContent="center">
      <Tooltip title="View Rates" arrow>
        <IconButton
          size="small"
          onClick={handleViewRates}
          sx={{
            color: "#667085",
            p: 0.5,
            "&:hover": {
              color: "#2469F0",
              bgcolor: alpha("#2469F0", 0.06),
            },
          }}
        >
          <ReceiptLongIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Edit" arrow>
        <IconButton
          size="small"
          onClick={() => handleEditClick(item.id)}
          sx={{
            color: "#667085",
            p: 0.5,
            "&:hover": {
              color: "#F59E0B",
              bgcolor: alpha("#F59E0B", 0.06),
            },
          }}
        >
          <EditIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete" arrow>
        <IconButton
          size="small"
          onClick={() => handleDeleteClick(item.id)}
          sx={{
            color: "#667085",
            p: 0.5,
            "&:hover": {
              color: "#EF4444",
              bgcolor: alpha("#EF4444", 0.06),
            },
          }}
        >
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      {/* PAGE HEADER */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "10px",
              bgcolor: alpha("#2469F0", 0.08),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BusinessIcon sx={{ color: "#2469F0", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: "#0f172a" }}>
              Billing & Funder Hub
            </Typography>
            <Typography variant="caption" color="#64748b" sx={{ fontSize: "0.75rem" }}>
              Manage Council Contracts and Brokerage Agency Profiles
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: 18 }} />}
          onClick={handleAddClick}
          size="small"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "6px",
            bgcolor: "#2469F0",
            px: 3,
            py: 0.75,
            "&:hover": {
              bgcolor: "#1A5BD4",
              boxShadow: "0px 2px 8px rgba(36, 105, 240, 0.3)",
            },
          }}
        >
          {activeTab === 0 ? "Connect New Council" : "Add Brokerage Agency"}
        </Button>
      </Box>

      {/* TABS */}
      <Paper
        sx={{
          borderRadius: "12px",
          mb: 3,
          overflow: "hidden",
          border: "1px solid #E2E8F0",
          boxShadow: "none",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.85rem",
              py: 1.5,
              minHeight: 56,
            },
          }}
        >
          <Tab
            icon={<BusinessIcon sx={{ fontSize: 18 }} />}
            label="Direct Councils"
            iconPosition="start"
          />
          <Tab
            icon={<HandshakeIcon sx={{ fontSize: 18 }} />}
            label="Brokerage Agencies"
            iconPosition="start"
          />
        </Tabs>

        {activeTab === 0 && (
          <Box sx={{ bgcolor: "#F8FAFC" }}>
            <Divider />
            <Tabs
              value={councilSubTab}
              onChange={handleSubTabChange}
              centered
              sx={{
                minHeight: "40px",
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontSize: "0.78rem",
                  minHeight: "40px",
                  fontWeight: 500,
                },
              }}
            >
              <Tab
                label="Authority List"
                icon={<ListAltIcon sx={{ fontSize: 16 }} />}
                iconPosition="start"
              />
              <Tab
                label="Rate Cards"
                icon={<RequestQuoteIcon sx={{ fontSize: 16 }} />}
                iconPosition="start"
              />
            </Tabs>
          </Box>
        )}
      </Paper>

      {/* CONTENT */}
      {activeTab === 0 && councilSubTab === 1 ? (
        <FunderRateCards funders={funders} isLoading={isLoading} />
      ) : (
        <Paper
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              bgcolor: "#F8FAFC",
              borderBottom: "1px solid #E2E8F0",
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: alpha(activeTab === 0 ? "#2469F0" : "#F59E0B", 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {activeTab === 0 ? (
                <ListAltIcon
                  sx={{ color: "#2469F0", fontSize: 18 }}
                />
              ) : (
                <HandshakeIcon
                  sx={{ color: "#F59E0B", fontSize: 18 }}
                />
              )}
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
                {activeTab === 0 ? "Authority List" : "Brokerage Agencies"}
              </Typography>
              <Typography variant="caption" color="#64748b" sx={{ fontSize: "0.7rem" }}>
                {activeTab === 0
                  ? "Manage direct council contracts"
                  : "Manage brokerage agency profiles"}
              </Typography>
            </Box>
          </Box>

          <DataTable
            data={funders}
            headers={headers}
            renderCell={renderCell}
            customActions={customActions}
            loading={isLoading}
            actionColumnLabel="Actions"
          />
        </Paper>
      )}

      <ConfirmModal
        show={confirmOpen}
        handleClose={() => setConfirmOpen(false)}
        handleConfirm={handleConfirmDelete}
        title="Delete Funder Profile?"
        body="Are you sure you want to delete this funder? This will remove all associated billing configurations and vendor details. This action cannot be undone."
        isLoading={deleteMutation.isLoading}
        confirmText="Delete Funder"
        isDelete={true}
      />

      <FunderDialog
        open={isDialogOpen}
        handleClose={() => setIsDialogOpen(false)}
        editData={selectedFunder}
        mode={category}
      />
    </Box>
  );
};

export default FunderManagement;